import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Music2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { TIMELINE, TIMELINE_INTRO, TIMELINE_ARTISTS } from "@/data/timeline";

const slug = (y) => "year-" + String(y).replace(/[^0-9a-z]/gi, "-").toLowerCase();

const HERO_IMG =
  "https://images.unsplash.com/photo-1782229296825-d5e4e98b90b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxwaWFuaXN0JTIwY2xhc3NpY2FsJTIwY29uY2VydCUyMGhhbGwlMjBncmFuZCUyMHBpYW5vJTIwbXVzaWNpYW4lMjBlZGl0b3JpYWx8ZW58MHx8fHwxNzkwODA0MzM0fDA&ixlib=rb-4.1.0&q=85";

export default function Timeline() {
  const [active, setActive] = useState(TIMELINE[0].year);

  const go = (y) => {
    setActive(y);
    const el = document.getElementById(slug(y));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border" data-testid="timeline-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Grand piano on a concert stage" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 sm:px-8 py-20 lg:py-28">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Pianist · Musical Director · Educator</p>
          <h1 className="mt-5 font-serif text-5xl font-light leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">The journey</h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">{TIMELINE_INTRO}</p>
        </div>
      </section>

      {/* Sticky year filter */}
      <div className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur-md" data-testid="timeline-yearnav">
        <div className="mx-auto max-w-6xl overflow-x-auto px-4 sm:px-8">
          <div className="flex gap-1 py-3">
            {TIMELINE.map((b) => (
              <button
                key={b.year}
                onClick={() => go(b.year)}
                data-testid={`timeline-year-${slug(b.year)}`}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === b.year ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {b.year}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Year bands (alternating light / dark) */}
      {TIMELINE.map((band, i) => {
        const dark = i % 2 === 1;
        return (
          <section
            key={band.year}
            id={slug(band.year)}
            data-testid={`timeline-band-${slug(band.year)}`}
            className={`scroll-mt-16 border-b ${dark ? "border-white/10 text-[#f2ede3]" : "border-border"}`}
            style={dark ? { background: "#121212" } : undefined}
          >
            <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-8 py-16 lg:grid-cols-12 lg:py-24">
              <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-24">
                  {band.highlight && (
                    <span className={`mb-3 inline-block font-mono text-[10px] uppercase tracking-[0.25em] ${dark ? "text-primary" : "text-primary"}`}>Career highlight</span>
                  )}
                  <h2 className={`font-serif text-5xl font-light tracking-tight sm:text-6xl ${dark ? "text-[#f2ede3]" : "text-foreground"}`}>{band.year}</h2>
                </div>
              </div>
              <div className="lg:col-span-8">
                <ul className="space-y-8">
                  {band.items.map((it, j) => (
                    <motion.li
                      key={j}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.4, delay: j * 0.05 }}
                      className={`border-l-2 pl-6 ${dark ? "border-primary/50" : "border-primary/40"}`}
                    >
                      <h3 className={`font-serif text-2xl leading-snug ${dark ? "text-[#f2ede3]" : "text-foreground"}`}>{it.title}</h3>
                      <p className={`mt-2 leading-relaxed ${dark ? "text-[#c9c2b6]" : "text-muted-foreground"}`}>{it.desc}</p>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        );
      })}

      {/* Shared the stage with */}
      <section className="mx-auto max-w-6xl px-4 sm:px-8 py-16 lg:py-20" data-testid="timeline-artists">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Met behind stage, shared the stage or studio with, or played for</p>
        <div className="mt-6 flex flex-wrap gap-2.5">
          {TIMELINE_ARTISTS.map((a) => (
            <span key={a} className="rounded-full border border-border bg-card px-4 py-1.5 text-sm">{a}</span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-card" data-testid="timeline-cta">
        <div className="mx-auto flex max-w-4xl flex-col items-start gap-6 px-4 sm:px-8 py-16 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-serif text-3xl tracking-tight sm:text-4xl">Learn from a life in music</h2>
            <p className="mt-3 text-muted-foreground">The same ears and hands behind these stages now teach you to read and play.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/courses" data-testid="timeline-courses-cta">
              <Button className="rounded-full bg-primary px-6 py-6 text-primary-foreground hover:bg-primary/90"><Music2 size={16} className="mr-2" /> Explore courses</Button>
            </Link>
            <Link to="/about" data-testid="timeline-about-cta">
              <Button variant="outline" className="rounded-full border-border px-6 py-6 hover:bg-secondary">Read the full story <ArrowRight size={16} className="ml-2" /></Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
